import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../app/routes';
import { FiUploadCloud, FiShare2, FiUsers, FiGlobe, FiChevronLeft, FiChevronDown, FiX, FiRefreshCw, FiCheck } from 'react-icons/fi';
import {
  submitPublishRequest, createShare, getShares, revokeShare, regenerateShare, refreshShare, installToolForAgency,
  type ShareScope,
} from '../services/myToolsApi';

export interface ShareableTool {
  id: string;
  name: string;
  // Matches every non-agent ToolType this modal is actually handed today
  // (MyToolsPage.tsx routes 'workflow'/'widget'/'integration' through here too,
  // via a cast — the modal never branches on the exact value, so this just
  // keeps the declared type honest about what it's given at runtime).
  type: 'skill' | 'mcp_server' | 'workflow' | 'widget' | 'integration';
  visibility: 'just_me' | 'my_agency' | 'published';
  publishStatus?: 'pending' | 'approved' | 'rejected' | 'changes_requested';
  isAgencyInstalled?: boolean;
}

interface ToolShareMenuModalProps {
  tool: ShareableTool;
  isAgencyOwner: boolean;
  onClose: () => void;
  /** Bumps the tool's own visibility to my_agency — delegates to whatever the
   * caller already uses to update a tool (e.g. the same onUpdate used elsewhere
   * in My Creations), rather than duplicating that call here. */
  onShareWithAgency: () => void;
  /** Present only when tool.isAgencyInstalled — swaps the Install-for-Agency row
   * for an Uninstall one, same as the old kebab menu's Install/Uninstall toggle. */
  onUninstallForAgency?: () => void;
  /** Opens the caller's install prompt for the agency (My Creations). Without it,
   * anything but a skill is sent to My Creations to install there - a direct install
   * would skip the agency's API key and an MCP server's shared fields. */
  onInstallForAgency?: () => void;
}

// My Creations' "Share" action for an already-existing skill or MCP server tool —
// the three-dot menu's Edit/Share/Delete set. Unlike SkillShareMenuModal (which
// mints a brand-new tool from an agent's live skill), everything here acts on the
// tool row that already exists, so there's no copy-then-share step for Publish/
// Share with Agency/Install for Agency. Share via Link still mints an independent
// copy server-side (CreateShare→CreateIndependentSkillCopy/CreateIndependentMcpCopy)
// so editing the original later never affects an already-shared link.
const ToolShareMenuModal: React.FC<ToolShareMenuModalProps> = ({ tool, isAgencyOwner, onClose, onShareWithAgency, onUninstallForAgency, onInstallForAgency }) => {
  const navigate = useNavigate();
  const isIntegration = tool.type === 'integration';
  const [view, setView] = useState<'menu' | 'link'>('menu');
  const [error, setError] = useState('');

  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  const [scope, setScope] = useState<ShareScope>('agency');
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkResult, setLinkResult] = useState<{ id: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  // Load any share already active for this tool, so re-opening "Share via Link"
  // shows the existing link (and lets it be copied/refreshed/revoked) instead of
  // only ever offering to mint a brand-new one on top of it — mirrors
  // AgentShareMenuModal's identical behavior for agent shares.
  useEffect(() => {
    let cancelled = false;
    getShares(tool.id)
      .then(shares => {
        if (cancelled) return;
        const existing = shares.filter(s => s.isActive).slice(-1)[0];
        if (existing) {
          setLinkResult({ id: existing.id, token: existing.token });
          setScope(existing.scope);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
  }, [tool.id]);

  const [refreshed, setRefreshed] = useState(false);
  const handleRefresh = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    setError('');
    try {
      await refreshShare(linkResult.id);
      setRefreshed(true);
      setTimeout(() => setRefreshed(false), 2500);
    } catch (e: any) {
      setError(e.message || 'Failed to refresh share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const [installingAgency, setInstallingAgency] = useState(false);
  const [agencyInstalled, setAgencyInstalled] = useState(false);

  const alreadyPublished = tool.visibility === 'published';
  const pendingReview = tool.publishStatus === 'pending';

  const handlePublish = async () => {
    setPublishing(true);
    setError('');
    try {
      await submitPublishRequest(tool.id);
      setPublished(true);
    } catch (e: any) {
      setError(e.message === 'already_pending' ? 'This tool already has a pending review request.' : (e.message || 'Failed to submit for review'));
    } finally {
      setPublishing(false);
    }
  };

  const handleCreateLink = async () => {
    setCreatingLink(true);
    setError('');
    try {
      const share = await createShare(tool.id, scope);
      setLinkResult({ id: share.id, token: share.token });
    } catch (e: any) {
      setError(e.message || 'Failed to create share link');
    } finally {
      setCreatingLink(false);
    }
  };

  const shareUrl = (token: string) => `${window.location.origin}/share/${token}`;

  const handleCopy = () => {
    if (!linkResult) return;
    navigator.clipboard?.writeText(shareUrl(linkResult.token)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    try {
      await revokeShare(linkResult.id);
      setLinkResult(null);
    } catch (e: any) {
      setError(e.message || 'Failed to revoke share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    try {
      const regenerated = await regenerateShare(linkResult.id);
      setLinkResult({ id: regenerated.id, token: regenerated.token });
    } catch (e: any) {
      setError(e.message || 'Failed to regenerate share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleInstallForAgency = async () => {
    if (onInstallForAgency) {
      onClose();
      onInstallForAgency();
      return;
    }
    if (tool.type !== 'skill') {
      onClose();
      navigate(`${ROUTES.MY_TOOLS}?view=creations&installForAgency=${encodeURIComponent(tool.id)}`);
      return;
    }
    setInstallingAgency(true);
    setError('');
    try {
      await installToolForAgency(tool.id);
      setAgencyInstalled(true);
    } catch (e: any) {
      setError(e.message || 'Failed to install for agency');
    } finally {
      setInstallingAgency(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(6px) saturate(1.2)', WebkitBackdropFilter: 'blur(6px) saturate(1.2)',
      zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--surface-card, #1a1a1f)',
        backdropFilter: 'blur(24px) saturate(1.3)', WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
        border: '1.5px solid var(--border-glass, rgba(255,255,255,0.1))', borderRadius: 18, padding: 24,
        width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 14,
        boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {view === 'link' && (
            <button onClick={() => setView('menu')} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: 'var(--text-secondary)', display: 'flex',
            }} aria-label="Back">
              <FiChevronLeft size={18} />
            </button>
          )}
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>
            {view === 'menu' ? `Share "${tool.name}"` : 'Share via Link'}
          </h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)',
            display: 'flex', padding: 4,
          }} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>

        {error && <p style={{ color: '#f87171', fontSize: 12.5, margin: 0 }}>{error}</p>}

        {view === 'menu' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* A Global API Integration is used by other agencies with their own key. */}
            <button onClick={handlePublish} disabled={publishing || published || alreadyPublished || pendingReview}
              style={{ ...menuRowStyle, opacity: publishing ? 0.6 : 1 }}>
              {published || pendingReview ? <FiCheck size={18} style={{ color: '#4ade80', flexShrink: 0 }} /> : <FiUploadCloud size={18} style={{ color: '#f97316', flexShrink: 0 }} />}
              <div style={{ textAlign: 'left' }}>
                <div style={menuRowTitleStyle}>
                  {publishing ? 'Publishing…' : alreadyPublished ? 'Published' : (published || pendingReview) ? 'Submitted for review' : 'Publish'}
                </div>
                {!published && !alreadyPublished && !pendingReview && <div style={menuRowSubStyle}>
                  {isIntegration
                    ? "Submit it for a platform admin's approval - other agencies call the API with their own key"
                    : "Submit this for a platform admin's approval"}
                </div>}
              </div>
            </button>

            <button onClick={() => { onShareWithAgency(); onClose(); }} disabled={tool.visibility !== 'just_me'} style={menuRowStyle}>
              <FiUsers size={18} style={{ color: '#4ade80', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={menuRowTitleStyle}>{tool.visibility === 'just_me' ? 'Share with Agency' : 'Shared with your agency'}</div>
                {tool.visibility === 'just_me' && <div style={menuRowSubStyle}>Makes this visible to everyone in your agency's marketplace</div>}
              </div>
            </button>

            {/* An integration's link copy never carries anyone's API key: whoever installs
                it adds their own. */}
            <button onClick={() => setView('link')} style={menuRowStyle}>
              <FiShare2 size={18} style={{ color: '#60a5fa', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={menuRowTitleStyle}>Share via Link</div>
                <div style={menuRowSubStyle}>
                  {isIntegration
                    ? 'Anyone with the link installs it and uses their own API key'
                    : 'Anyone with the link installs their own copy'}
                </div>
              </div>
            </button>

            {isAgencyOwner && (
              tool.isAgencyInstalled ? (
                <button onClick={() => { onUninstallForAgency?.(); onClose(); }} style={{ ...menuRowStyle }}
                  title="Remove this tool from every user in your agency">
                  <FiUsers size={18} style={{ color: '#f87171', flexShrink: 0 }} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={menuRowTitleStyle}>Uninstall for Agency</div>
                  </div>
                </button>
              ) : (
                <button onClick={handleInstallForAgency} disabled={installingAgency || agencyInstalled || tool.visibility === 'just_me'}
                  title={tool.visibility === 'just_me' ? 'Share with Agency first' : undefined}
                  style={{ ...menuRowStyle, opacity: installingAgency ? 0.6 : 1 }}>
                  {agencyInstalled ? <FiCheck size={18} style={{ color: '#4ade80', flexShrink: 0 }} /> : <FiUsers size={18} style={{ color: '#4ade80', flexShrink: 0 }} />}
                  <div style={{ textAlign: 'left' }}>
                    <div style={menuRowTitleStyle}>
                      {installingAgency ? 'Installing for agency…' : agencyInstalled ? 'Installed for your agency' : 'Install for Agency'}
                    </div>
                    {!agencyInstalled && <div style={menuRowSubStyle}>Install this for everyone in your agency</div>}
                  </div>
                </button>
              )
            )}
          </div>
        )}

        {view === 'link' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Anyone who opens the link installs their own independent copy automatically — no separate
              import step. Works on drafts; sharing doesn't go through review.
            </p>

            {loadingExisting && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 12.5, margin: 0 }}>Checking for an existing link…</p>
            )}

            {!loadingExisting && !linkResult && (
              <>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>General access</div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)',
                    }}>
                      {scope === 'anyone' ? <FiGlobe size={17} /> : <FiUsers size={17} />}
                    </div>
                    <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                      <button type="button" onClick={() => setScopeMenuOpen(o => !o)} style={dropdownBtnStyle}>
                        <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-primary)' }}>
                          {scope === 'anyone' ? 'Anyone with the link' : 'Anyone in this agency'}
                        </span>
                        <FiChevronDown size={15} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                      </button>
                      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 4 }}>
                        {scope === 'anyone'
                          ? 'With this link, even agents outside your agency install their own copy'
                          : 'Anyone in your agency with this link can install their own copy'}
                      </div>

                      {scopeMenuOpen && (
                        <div style={dropdownMenuStyle}>
                          <button type="button" onClick={() => { setScope('agency'); setScopeMenuOpen(false); }} style={dropdownItemStyle}>
                            <FiUsers size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left', flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>Anyone in this agency</div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Only people in your agency with the link</div>
                            </div>
                            {scope === 'agency' && <FiCheck size={15} style={{ color: 'var(--accent-purple-deep)', flexShrink: 0 }} />}
                          </button>
                          <button type="button" onClick={() => { setScope('anyone'); setScopeMenuOpen(false); }} style={{ ...dropdownItemStyle, borderBottom: 'none' }}>
                            <FiGlobe size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left', flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>Anyone with the link</div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Even agents outside your agency, with the link</div>
                            </div>
                            {scope === 'anyone' && <FiCheck size={15} style={{ color: 'var(--accent-purple-deep)', flexShrink: 0 }} />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setView('menu')} disabled={creatingLink}>
                    Cancel
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleCreateLink} disabled={creatingLink}>
                    {creatingLink ? <><FiRefreshCw size={12} className="ca-fp-spin" /> Creating…</> : 'Create link'}
                  </button>
                </div>
              </>
            )}

            {linkResult && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)',
                  }}>
                    {scope === 'anyone' ? <FiGlobe size={17} /> : <FiUsers size={17} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-primary)' }}>
                      {scope === 'anyone' ? 'Anyone with the link' : 'Anyone in this agency'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>can install their own copy</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input readOnly value={shareUrl(linkResult.token)} className="ca-fp-editor__textarea"
                    style={{ minHeight: 'auto', height: 38, padding: '8px 12px', fontSize: 12, flex: 1 }}
                    onClick={e => (e.target as HTMLInputElement).select()} />
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                </div>
                <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                  Edited the tool since sharing this? <strong>Refresh</strong> updates the link's content in
                  place — same URL, nothing breaks for people who already have it.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" disabled={linkBusy} onClick={handleRefresh}>
                    {linkBusy ? 'Working…' : refreshed ? 'Refreshed!' : 'Refresh'}
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" disabled={linkBusy} onClick={handleRegenerate}>
                    {linkBusy ? 'Working…' : 'Regenerate'}
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" disabled={linkBusy} onClick={handleRevoke}>
                    {linkBusy ? 'Working…' : 'Revoke'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const menuRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
  background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
  borderRadius: 11, cursor: 'pointer', textAlign: 'left', width: '100%',
};
const menuRowTitleStyle: React.CSSProperties = { fontSize: 13.5, fontWeight: 650, color: 'var(--text-primary)' };
const menuRowSubStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 };

const dropdownBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%',
  padding: '8px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
  background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
};
const dropdownMenuStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 10,
  background: 'var(--surface-card, #1a1a1f)', border: '1px solid var(--border-glass)', borderRadius: 10,
  boxShadow: '0 16px 40px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
};
const dropdownItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', width: '100%',
  background: 'none', border: 'none', borderBottom: '1px solid var(--border-glass)', cursor: 'pointer', textAlign: 'left',
};

export default ToolShareMenuModal;
